import { useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';
import axios from 'axios';

type Preview = { total: number; preview: { row: number; name: string; company: string }[] };
type Result = { total: number; created: number; skippedCount: number; skipped: { row: number; message: string }[] };
function newRequestId() {
  const bytes = crypto.getRandomValues(new Uint8Array(16)); bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
export function PoolImport({ api, onImported }: { api: AxiosInstance; onImported: (message: string) => void }) {
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [departmentId, setDepartmentId] = useState('');
  const [fileData, setFileData] = useState<{ csv: string } | { xlsx: string } | null>(null);
  const [requestId, setRequestId] = useState(newRequestId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [issues, setIssues] = useState<{ row: number; message: string }[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    api.get('/api/public-customers/import/options', { signal: controller.signal }).then(response => {
      setDepartments(response.data.departments); if (response.data.departments.length === 1) setDepartmentId(response.data.departments[0].id);
    }).catch(error => { if (!axios.isCancel(error)) setError('所属部门加载失败，请刷新页面重试'); });
    return () => controller.abort();
  }, [api]);
  function reset() { setPreview(null); setResult(null); setIssues([]); setError(''); setRequestId(newRequestId()); }
  async function fileChanged(file?: File) {
    reset(); setFileData(null); if (!file) return;
    setBusy(true);
    try {
      const extension = file.name.toLowerCase().split('.').pop();
      if (!['csv', 'xlsx'].includes(extension ?? '') || !file.size || file.size > 500000) throw Error();
      const buffer = await file.arrayBuffer();
      if (extension === 'xlsx') {
        const bytes = new Uint8Array(buffer); let binary = '';
        for (let offset = 0; offset < bytes.length; offset += 8192) binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
        setFileData({ xlsx: btoa(binary) });
      } else setFileData({ csv: new TextDecoder('utf-8', { fatal: true }).decode(buffer) });
    } catch { setError('文件读取失败，请上传不超过 500KB 的 .xlsx 文件或 CSV UTF-8 文件'); }
    finally { setBusy(false); }
  }
  async function download() {
    setBusy(true); setError('');
    try {
      const response = await api.get('/api/public-customers/import/template', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data); const link = document.createElement('a'); link.href = url; link.download = '公共客户池导入模板.xlsx'; link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { setError('模板下载失败，请重试'); } finally { setBusy(false); }
  }
  async function submit(importNow: boolean) {
    if (!fileData) return;
    setBusy(true); setError(''); setIssues([]);
    try {
      const response = await api.post(`/api/public-customers/import${importNow ? '' : '/preview'}`, { ...fileData, departmentId, requestId });
      if (importNow) { setResult(response.data); onImported(`导入完成：新增 ${response.data.created} 个客户，跳过 ${response.data.skippedCount} 行`); }
      else setPreview(response.data);
    } catch (error) {
      setError(axios.isAxiosError(error) ? error.response?.data?.message || '请求失败，请重试' : '操作失败');
      if (axios.isAxiosError(error)) setIssues(error.response?.data?.errors || []);
    } finally { setBusy(false); }
  }
  return <details className="pool-import biz-page" style={{ padding: 16, borderBottom: '1px solid var(--border)' }}>
    <summary style={{ cursor: 'pointer', fontWeight: 600 }}>导入客户 / 下载模板</summary>
    <p style={{ marginTop: 16 }}>管理员批量导入公共池客户，业务员可在所属业务部门公共池认领后跟进。认领前联系方式继续脱敏。</p>
    <p>客户名称必填，并至少填写电话、微信、旺旺、邮箱、WhatsApp 中的一项。每批最多 500 行；相同联系方式自动跳过，不覆盖已有客户。等级可填 VIP、一般客户、潜在客户，留空为一般客户。</p>
    <p>请下载 Excel 模板填写：蓝色单元格可下拉选择客户等级、来源、客户群体和业务渠道，电话和账号列已设为文本。业务渠道可用顿号（、）或分号（；）分隔多个选项；来源选择“其他渠道”时，必须填写新增第 19 列“来源备注”。保留原表头，保存为 .xlsx 后直接上传；旧 18 列模板和 CSV UTF-8 文件仍可导入。</p>
    <fieldset disabled={busy} style={{ border: 0, padding: 0, margin: '16px 0' }}>
      <div className="biz-toolbar">
        <button type="button" className="ghost" onClick={() => void download()}>下载导入模板</button>
        <label>导入所属部门<select aria-label="导入所属部门" value={departmentId} onChange={event => { setDepartmentId(event.target.value); reset(); }}><option value="">请选择所属部门</option>{departments.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label>客户导入文件<input aria-label="客户导入文件" type="file" accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" style={{ maxWidth: '100%' }} onChange={event => void fileChanged(event.target.files?.[0])} /></label>
        <button type="button" disabled={!fileData || !departmentId || Boolean(result)} onClick={() => void submit(false)}>校验文件</button>
      </div>
    </fieldset>
    {!departments.length && !error && <p>暂无可导入的启用部门，请先配置组织架构。</p>}
    {error && <p role="alert">{error}</p>}
    {issues.length > 0 && <ul>{issues.map(item => <li key={item.row}>第 {item.row} 行：{item.message}</li>)}</ul>}
    {preview && !result && <div><p>校验通过，共 {preview.total} 个客户。以下为前 10 行预览，确认后导入所选部门公共池。</p><div className="table-wrap"><table><thead><tr><th>文件行</th><th>客户名称</th><th>公司</th></tr></thead><tbody>{preview.preview.map(row => <tr key={row.row}><td>{row.row}</td><td>{row.name}</td><td>{row.company || '—'}</td></tr>)}</tbody></table></div><button type="button" disabled={busy} onClick={() => void submit(true)}>{busy ? '导入中…' : `确认导入 ${preview.total} 个客户`}</button></div>}
    {result && <div role="status"><p>导入完成：新增 {result.created} 个客户，跳过 {result.skippedCount} 行。</p>{result.skipped.length > 0 && <details><summary>查看跳过原因</summary><ul>{result.skipped.map(row => <li key={row.row}>第 {row.row} 行：{row.message}</li>)}</ul></details>}</div>}
  </details>;
}
